import React, { useCallback, useEffect, useRef, useState } from 'react'
import { trackSignUp } from '../util/Analytics'
import { PrimaryButton, SecondaryButton } from '../components/Button'
import {
  GOOGLE_RECAPTCHA_SITE_KEY,
  RADOM_COLORS,
  RADOM_TOKEN_EXPIRY_LOCAL_STORAGE_KEY,
  RADOM_TOKEN_LOCAL_STORAGE_KEY
} from '../util/Constants'
import { observer } from 'mobx-react'
import { InputLabel, TextInput } from './Input'
import Radom from '../state/Radom'
import Close from '../icons/Close'
import { useSearchParams } from 'react-router-dom'
import { clearSessionLocalStorage, errorToast, successToast } from '../util/Util'
import styled from 'styled-components'
import Checkmark from '../icons/AnimatedCheckmark'
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { LoginStep } from './WithAuthentication'

const LoginButton = styled(PrimaryButton)`
  width: 100%;
  border-radius: 12px;
  min-height: 42px;
  padding: 0 18px !important;
  font-size: 13.75px;
  font-weight: 465;
`

const AuthForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
  font-size: 13.75px;
`

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`

const FormMetaRow = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  line-height: 1.35;
`

const AuthLabel = styled(InputLabel)`
  color: #4f5565;
  font-size: 12.75px;
  font-weight: 410;
`

const AuthTextInput = styled(TextInput)`
  min-height: 44px;
  border-radius: 12px;
  padding: 8px 12px;
  font-size: 14px;
  color: ${RADOM_COLORS.BLACK};
`

const FormHelperText = styled.div`
  color: ${RADOM_COLORS.GRAY_DARKEST};
  font-size: 12px;
  line-height: 1.5;
  text-align: center;
  max-width: 306px;
  margin: 2px auto 0;
`

const TextLinkButton = styled(SecondaryButton)`
  padding: 0;
  border: 0;
  display: inline-flex;
  justify-content: flex-start;
  width: fit-content;
  min-height: 0;
  color: ${RADOM_COLORS.NEW_BLUE};
  font-size: 12.5px;
  font-weight: 450;
  background: transparent;

  &:hover {
    background: transparent;
    border-color: transparent;
    text-decoration: underline;
  }
`

const StatusPanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
  padding: 4px 0 2px;
`

const StatusNote = styled.div`
  color: ${RADOM_COLORS.GRAY_DARKEST};
  font-size: 12.5px;
  line-height: 1.5;
  max-width: 300px;
`

const StatusEmail = styled.span`
  color: ${RADOM_COLORS.NEW_GRAY_1};
  font-weight: 460;
  font-size: 13px;
`

interface IProps {
  step: LoginStep
  setStep: (step: LoginStep) => void
}

type RecaptchaExecutor = (action: string) => Promise<string>

const EmailLoginInner = observer((props: IProps) => {
  const { executeRecaptcha } = useGoogleReCaptcha()
  const executeRecaptchaRef = useRef<RecaptchaExecutor | null>(
    executeRecaptcha ? async (action: string): Promise<string> => await executeRecaptcha(action) : null
  )
  const recaptchaScriptLoadPromiseRef = useRef<Promise<void> | null>(null)

  const step = props.step

  const [err, setErr] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')
  const [code, setPin] = useState('')

  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    executeRecaptchaRef.current = executeRecaptcha
      ? async (action: string): Promise<string> => await executeRecaptcha(action)
      : null
  }, [executeRecaptcha])

  const getWindowRecaptchaExecutor = useCallback((): RecaptchaExecutor | null => {
    const windowGreCaptcha = (window as any)?.grecaptcha
    if (!windowGreCaptcha) {
      return null
    }

    if (
      windowGreCaptcha.enterprise &&
      typeof windowGreCaptcha.enterprise.ready === 'function' &&
      typeof windowGreCaptcha.enterprise.execute === 'function'
    ) {
      return async (action: string): Promise<string> => {
        return await new Promise((resolve, reject) => {
          windowGreCaptcha.enterprise.ready(async () => {
            try {
              const token = await windowGreCaptcha.enterprise.execute(GOOGLE_RECAPTCHA_SITE_KEY, { action })
              resolve(token)
            } catch (err) {
              reject(err)
            }
          })
        })
      }
    }

    if (
      typeof windowGreCaptcha.ready === 'function' &&
      typeof windowGreCaptcha.execute === 'function'
    ) {
      return async (action: string): Promise<string> => {
        return await new Promise((resolve, reject) => {
          windowGreCaptcha.ready(async () => {
            try {
              const token = await windowGreCaptcha.execute(GOOGLE_RECAPTCHA_SITE_KEY, { action })
              resolve(token)
            } catch (err) {
              reject(err)
            }
          })
        })
      }
    }

    return null
  }, [])

  const ensureRecaptchaScript = useCallback(async (): Promise<void> => {
    const existingExecutor = getWindowRecaptchaExecutor()
    if (existingExecutor) {
      return
    }

    if (!recaptchaScriptLoadPromiseRef.current) {
      const recaptchaHosts = ['https://www.google.com', 'https://www.recaptcha.net']

      const loadScript = async (id: string, src: string): Promise<void> => {
        await new Promise<void>((resolve, reject) => {
          const existingScript = document.getElementById(id) as HTMLScriptElement | null
          if (existingScript) {
            if (getWindowRecaptchaExecutor()) {
              resolve()
              return
            }

            existingScript.remove()
          }

          const script = document.createElement('script')
          script.id = id
          script.src = src
          script.async = true
          script.defer = true
          script.onload = () => resolve()
          script.onerror = () => reject(new Error(`Failed to load script: ${src}`))

          document.head.appendChild(script)
        })
      }

      const loadScriptFromHosts = async (
        idPrefix: string,
        pathWithQuery: string
      ): Promise<void> => {
        let lastError: Error | null = null

        for (const host of recaptchaHosts) {
          const hostSuffix = host.includes('google.com') ? 'google' : 'recaptcha-net'
          const id = `${idPrefix}-${hostSuffix}`
          const src = `${host}${pathWithQuery}`

          try {
            await loadScript(id, src)
            if (getWindowRecaptchaExecutor()) {
              return
            }
          } catch (err) {
            if (err instanceof Error) {
              lastError = err
            } else {
              lastError = new Error(String(err))
            }
          }
        }

        if (lastError) {
          throw lastError
        }

        throw new Error(`Failed to load reCAPTCHA script: ${pathWithQuery}`)
      }

      recaptchaScriptLoadPromiseRef.current = (async () => {
        const encodedSiteKey = encodeURIComponent(GOOGLE_RECAPTCHA_SITE_KEY)
        await loadScriptFromHosts(
          'radom-recaptcha-fallback-script',
          `/recaptcha/api.js?render=${encodedSiteKey}`
        )

        if (getWindowRecaptchaExecutor()) {
          return
        }

        await loadScriptFromHosts(
          'radom-recaptcha-fallback-enterprise-script',
          `/recaptcha/enterprise.js?render=${encodedSiteKey}`
        )
      })()
        .catch(err => {
          recaptchaScriptLoadPromiseRef.current = null
          throw err
        })
    }

    await recaptchaScriptLoadPromiseRef.current
  }, [getWindowRecaptchaExecutor])

  const getErrorMessage = useCallback((error: unknown): string => {
    if (typeof error === 'object' && error !== null) {
      const maybeAxios = error as {
        response?: { data?: { error?: string } }
        reason?: string
        message?: string
      }

      return maybeAxios.response?.data?.error || maybeAxios.reason || maybeAxios.message || 'Something went wrong'
    }

    if (typeof error === 'string') {
      return error
    }

    return 'Something went wrong'
  }, [])

  const waitForRecaptcha = useCallback(async (): Promise<RecaptchaExecutor> => {
    const timeoutMs = 10000
    const pollMs = 100
    const start = Date.now()
    let triedFallbackScriptLoad = false

    while (Date.now() - start < timeoutMs) {
      const execute = executeRecaptchaRef.current
      if (execute) {
        return execute
      }

      const windowExecutor = getWindowRecaptchaExecutor()
      if (windowExecutor) {
        return windowExecutor
      }

      if (!triedFallbackScriptLoad) {
        triedFallbackScriptLoad = true
        try {
          await ensureRecaptchaScript()
        } catch (err) {
          console.error('Failed to bootstrap reCAPTCHA script:', err)
        }
      }

      await new Promise(resolve => setTimeout(resolve, pollMs))
    }

    throw new Error('Security check failed to initialize. Please disable blockers and refresh.')
  }, [ensureRecaptchaScript, getWindowRecaptchaExecutor])

  const getRecaptchaToken = useCallback(async (action: string): Promise<string> => {
    try {
      const execute = executeRecaptchaRef.current || await waitForRecaptcha()
      const token = await execute(action)
      if (!token) {
        throw new Error('Security check failed. Please refresh and try again.')
      }

      return token
    } catch (err) {
      const message = getErrorMessage(err)
      errorToast(message)
      throw new Error(message)
    }
  }, [getErrorMessage, waitForRecaptcha])

  useEffect(() => {
    const action = searchParams.get('action')
    const urlEmail = searchParams.get('email')
    const urlPin = searchParams.get('code')
    if (urlEmail && urlPin) {
      setEmail(urlEmail)
      setPin(urlPin)
      if (action === 'reset_password') {
        props.setStep(LoginStep.PasswordResetCodeVerified)
      } else {
        props.setStep(LoginStep.CompleteSignUp)
      }
    }
  }, [searchParams])

  useEffect(() => {
    setErr('')
  }, [step])

  const completeSignUp = async (): Promise<void> => {
    if (password !== passwordConfirm) {
      return setErr('Passwords do not match')
    }

    setErr('')
    setIsLoading(true)

    Radom.completeEmailSignUp(email, password, code)
      .then(res => {
        trackSignUp('email')
        Radom.token = res.jwtToken
        try {
          localStorage.setItem(RADOM_TOKEN_LOCAL_STORAGE_KEY, Radom.token)
          localStorage.setItem(RADOM_TOKEN_EXPIRY_LOCAL_STORAGE_KEY, new Date(res.expiresAt).toString())
        } catch (err) {
          clearSessionLocalStorage()
          window.location = '/' as any
        }
        setSearchParams({})

        Radom.isLoggedIn = !res.requires2fa
        Radom.requires2fa = res.requires2fa
        if (!Radom.requires2fa) {
          successToast('Successfully signed in!')
        }
      })
      .catch(err => {
        setErr(getErrorMessage(err))
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const login = async (): Promise<void> => {
    setErr('')
    setIsLoading(true)

    try {
      const recaptchaToken = await getRecaptchaToken('email_login')
      const res = await Radom.emailSignIn(email, password, recaptchaToken)
      Radom.token = res.jwtToken
      try {
        localStorage.setItem(RADOM_TOKEN_LOCAL_STORAGE_KEY, Radom.token)
        localStorage.setItem(RADOM_TOKEN_EXPIRY_LOCAL_STORAGE_KEY, new Date(res.expiresAt).toString())
      } catch (err) {
        clearSessionLocalStorage()
        window.location = '/' as any
      }
      setSearchParams({})

      Radom.isLoggedIn = !res.requires2fa
      Radom.requires2fa = res.requires2fa
      if (!res.requires2fa) {
        successToast('Successfully signed in!')
      }
    } catch (err) {
      setErr(getErrorMessage(err))
    } finally {
      setIsLoading(false)
    }
  }

  const statusCopy = (() => {
    switch (step) {
    case LoginStep.VerifySignUp:
      return {
        note: 'Use the verification link in your inbox to continue creating your account.',
        emailLabel: email ? `Sent to ${email}` : ''
      }
    case LoginStep.PasswordResetEmailSent:
      return {
        note: 'Open the reset link in your inbox, then return here to set your new password.',
        emailLabel: email ? `Sent to ${email}` : ''
      }
    default:
      return {
        note: '',
        emailLabel: ''
      }
    }
  })()

  const resetPassword = async (): Promise<void> => {
    setErr('')
    setIsLoading(true)
    try {
      const recaptchaToken = await getRecaptchaToken('email_start_password_reset')
      await Radom.startPasswordReset(email, recaptchaToken)
      props.setStep(LoginStep.PasswordResetEmailSent)
    } catch (err) {
      setErr(getErrorMessage(err))
    } finally {
      setIsLoading(false)
    }
  }

  const startSignUp = async (): Promise<void> => {
    setErr('')
    setIsLoading(true)
    try {
      const recaptchaToken = await getRecaptchaToken('email_start_signup')
      await Radom.startEmailSignUp(email, recaptchaToken)
      props.setStep(LoginStep.VerifySignUp)
    } catch (err) {
      setErr(getErrorMessage(err))
    } finally {
      setIsLoading(false)
    }
  }

  const completePasswordReset = async (): Promise<void> => {
    setIsLoading(true)
    setErr('')
    Radom.completePasswordReset(email, password, code)
      .then(res => {
        Radom.token = res.jwtToken
        try {
          localStorage.setItem(RADOM_TOKEN_LOCAL_STORAGE_KEY, Radom.token)
          localStorage.setItem(RADOM_TOKEN_EXPIRY_LOCAL_STORAGE_KEY, new Date(res.expiresAt).toString())
        } catch (err) {
          clearSessionLocalStorage()
          window.location = '/' as any
        }
        setSearchParams({})
        Radom.isLoggedIn = !res.requires2fa
        Radom.requires2fa = res.requires2fa
        if (!res.requires2fa) {
          successToast('Successfully signed in!')
        }
      })
      .catch(err => {
        setErr(getErrorMessage(err))
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  return <div style={{
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    width: '100%',
    fontSize: 13.5
  }}>

    {
      err &&
        <div style={{
          backgroundColor: '#fff3f2',
          border: '1px solid #f3c7c2',
          borderRadius: 12,
          padding: '12px 14px',
          color: '#8f2f24',
          display: 'flex',
          alignItems: 'center',
          gap: 10
        }}>
          <div style={{
            backgroundColor: '#fbe2df',
            width: 18,
            height: 18,
            borderRadius: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Close style={{ width: 8 }} stroke='#b63b2d' strokeWidth={2} />
          </div>
          <span>{err}</span>
        </div>
    }

    {
      (step === LoginStep.VerifySignUp || step === LoginStep.PasswordResetEmailSent) &&
        <StatusPanel>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: RADOM_COLORS.SUCCESS,
            width: 38,
            height: 38,
            borderRadius: '100%'
          }}>
            <Checkmark
              animationSpeed={'1s'}
              animationDelay={'0s'}
              color='white'
            />
          </div>
          {statusCopy.emailLabel && <StatusEmail>{statusCopy.emailLabel}</StatusEmail>}
          <StatusNote>{statusCopy.note}</StatusNote>
        </StatusPanel>
    }

    {
      step === LoginStep.Login &&
        <AuthForm
          onSubmit={e => {
            e.preventDefault()
            void login()
          }}>
          <FieldGroup>
            <AuthLabel>Email address</AuthLabel>
            <AuthTextInput
              autoComplete='email'
              autoFocus
              placeholder='Enter email address'
              value={email}
              onChange={e => setEmail(e.target.value)}
              disabled={isLoading} />
          </FieldGroup>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <FieldGroup>
              <AuthLabel>Password</AuthLabel>
              <AuthTextInput
                type='password'
                autoComplete='current-password'
                placeholder='Enter password'
                value={password}
                disabled={isLoading}
                onChange={e => setPassword(e.target.value)} />
            </FieldGroup>

            <FormMetaRow>
              <span style={{ color: RADOM_COLORS.BLACK, fontSize: 12.5, fontWeight: 400 }}>Forgot your password?</span>
              <TextLinkButton
                type='button'
                disabled={isLoading}
                onClick={() => props.setStep(LoginStep.ForgotPassword)}>
                Reset it
              </TextLinkButton>
            </FormMetaRow>
          </div>

          <LoginButton
            type='submit'
            isLoading={isLoading}
            style={{ marginTop: 2 }}>
            <span>Continue</span>
          </LoginButton>
        </AuthForm>
    }

    {
      step === LoginStep.StartSignUp &&
        <AuthForm
          onSubmit={e => {
            e.preventDefault()
            void startSignUp()
          }}>
          <FieldGroup>
            <AuthLabel>Email address</AuthLabel>
            <AuthTextInput
              autoComplete='email'
              autoFocus
              placeholder='Enter email address'
              value={email}
              disabled={isLoading}
              onChange={e => setEmail(e.target.value)} />
          </FieldGroup>

          <LoginButton
            type='submit'
            isLoading={isLoading}
            style={{ marginTop: 2 }}>
            <span>Continue</span>
          </LoginButton>

          <FormHelperText>
            We&apos;ll send a verification link to this inbox so you can finish creating your account.
          </FormHelperText>
        </AuthForm>
    }

    {
      step === LoginStep.PasswordResetCodeVerified &&
        <AuthForm
          onSubmit={e => {
            e.preventDefault()
            void completePasswordReset()
          }}>
          <FieldGroup>
            <AuthLabel>Password</AuthLabel>
            <AuthTextInput
              type='password'
              autoComplete='new-password'
              autoFocus
              placeholder='Enter password'
              value={password}
              disabled={isLoading}
              onChange={e => setPassword(e.target.value)} />
          </FieldGroup>

          <FieldGroup>
            <AuthLabel>Confirm password</AuthLabel>
            <AuthTextInput
              type='password'
              autoComplete='new-password'
              placeholder='Confirm password'
              value={passwordConfirm}
              disabled={isLoading}
              onChange={e => setPasswordConfirm(e.target.value)} />
          </FieldGroup>

          <LoginButton
            type='submit'
            isLoading={isLoading}
            style={{ marginTop: 2 }}>
            <span>Set password</span>
          </LoginButton>
        </AuthForm>
    }

    {
      step === LoginStep.ForgotPassword &&
        <AuthForm
          onSubmit={e => {
            e.preventDefault()
            void resetPassword()
          }}>
          <FieldGroup>
            <AuthLabel>Email address</AuthLabel>
            <AuthTextInput
              autoComplete='email'
              autoFocus
              placeholder='Enter email address'
              value={email}
              disabled={isLoading}
              onChange={e => setEmail(e.target.value)} />
          </FieldGroup>

          <LoginButton
            type='submit'
            isLoading={isLoading}
            style={{ marginTop: 2 }}>
            <span>Reset password</span>
          </LoginButton>

          <FormHelperText>
            We&apos;ll email secure reset instructions to the address above.
          </FormHelperText>
        </AuthForm>
    }

    {
      step === LoginStep.CompleteSignUp &&
        <AuthForm
          onSubmit={e => {
            e.preventDefault()
            void completeSignUp()
          }}>
          <FieldGroup>
            <AuthLabel>Email address</AuthLabel>
            <AuthTextInput
              placeholder='Enter email address'
              value={email}
              disabled />
          </FieldGroup>

          <FieldGroup>
            <AuthLabel>Password</AuthLabel>
            <AuthTextInput
              type='password'
              autoComplete='new-password'
              autoFocus
              placeholder='Enter password'
              value={password}
              disabled={isLoading}
              onChange={e => setPassword(e.target.value)} />
          </FieldGroup>

          <FieldGroup>
            <AuthLabel>Confirm password</AuthLabel>
            <AuthTextInput
              type='password'
              autoComplete='new-password'
              placeholder='Confirm password'
              value={passwordConfirm}
              disabled={isLoading}
              onChange={e => setPasswordConfirm(e.target.value)} />
          </FieldGroup>

          <LoginButton
            type='submit'
            isLoading={isLoading}
            style={{ marginTop: 2 }}>
            Continue
          </LoginButton>
        </AuthForm>
    }
  </div>
})

const EmailLogin = (props: IProps): JSX.Element => {
  return <GoogleReCaptchaProvider
    reCaptchaKey={GOOGLE_RECAPTCHA_SITE_KEY}
    scriptProps={{ async: true, defer: true, appendTo: 'head' }}>
    <EmailLoginInner {...props} />
  </GoogleReCaptchaProvider>
}

export default EmailLogin
